import { endpoints } from '../config/endpoints';
import { apiFetch } from './client';
import type { Agent, AgentWithRelations, CreateAgentRequest, UpdateAgentRequest } from '../types/agent.types';

export async function fetchAgents(
  workspaceId: string,
  params?: { search?: string; limit?: number; offset?: number }
): Promise<{ agents: Agent[]; total: number }> {
  const queryParams = new URLSearchParams();

  if (params?.search) queryParams.set('search', params.search);
  if (params?.limit) queryParams.set('limit', params.limit.toString());
  if (params?.offset) queryParams.set('offset', params.offset.toString());

  const url = `${endpoints.agents.list(workspaceId)}${queryParams.toString() ? `?${queryParams}` : ''}`;
  const response = await apiFetch(url);

  if (!response.ok) {
    throw new Error(`Failed to fetch agents: ${response.statusText}`);
  }

  const result = await response.json();
  // Backend returns { data: [...], pagination: {...} }
  return {
    agents: result.data || [],
    total: result.pagination?.totalCount ?? (result.data?.length || 0),
  };
}

export async function fetchAgent(workspaceId: string, id: string): Promise<AgentWithRelations> {
  const response = await apiFetch(endpoints.agents.getById(workspaceId, id));

  if (!response.ok) {
    throw new Error(`Failed to fetch agent: ${response.statusText}`);
  }

  return response.json();
}

export async function createAgent(workspaceId: string, data: CreateAgentRequest): Promise<Agent> {
  const response = await apiFetch(endpoints.agents.create(workspaceId), {
    method: 'POST',
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.error || `Failed to create agent: ${response.statusText}`);
  }

  return response.json();
}

export async function updateAgent(workspaceId: string, id: string, data: UpdateAgentRequest): Promise<Agent> {
  const response = await apiFetch(endpoints.agents.update(workspaceId, id), {
    method: 'PUT',
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.error || `Failed to update agent: ${response.statusText}`);
  }

  return response.json();
}

export async function deleteAgent(workspaceId: string, id: string): Promise<void> {
  const response = await apiFetch(endpoints.agents.delete(workspaceId, id), {
    method: 'DELETE',
  });

  if (!response.ok) {
    throw new Error(`Failed to delete agent: ${response.statusText}`);
  }
}

export async function updateAgentConnectors(
  workspaceId: string,
  agentId: string,
  connectors: Array<{ connectorId: number; isActive: boolean; config?: { disabledTools?: string[]; autoApprovedTools?: string[] } }>
): Promise<void> {
  const response = await apiFetch(endpoints.agents.connectors(workspaceId, agentId), {
    method: 'PUT',
    body: JSON.stringify(connectors),
  });

  if (!response.ok) {
    throw new Error(`Failed to update agent connectors: ${response.statusText}`);
  }
}

export async function updateAgentSkills(
  workspaceId: string,
  agentId: string,
  skills: Array<{ skillId: string; isActive: boolean }>
): Promise<void> {
  const response = await apiFetch(endpoints.agents.skills(workspaceId, agentId), {
    method: 'PUT',
    body: JSON.stringify(skills),
  });

  if (!response.ok) {
    throw new Error(`Failed to update agent skills: ${response.statusText}`);
  }
}
